import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./services.css";
import { Search, Briefcase, Globe, Smartphone, UserCheck, BarChart3, Sparkles } from "lucide-react";

const svcIconMap = {
  Search: <Search size={24} />,
  Briefcase: <Briefcase size={24} />,
  Globe: <Globe size={24} />,
  Smartphone: <Smartphone size={24} />,
  UserCheck: <UserCheck size={24} />,
  BarChart3: <BarChart3 size={24} />,
};

const servicesData = [
  {
  id: 1,
  icon: "Search",
  title: "Executive Search",
  tagline: "Build Superior Leadership Teams",
  description:
    "EduITalent's Executive Search services help companies identify and place exceptional leaders at board and top management levels. We leverage technology and deep industry expertise to fulfill your leadership needs with precision — from sourcing to onboarding — while managing all administrative details and logistics.",
  bullets: [
    "Placement for C-suite, VP, Country Heads & General Managers",
    "End-to-end HR outsourcing: recruitment to performance management",
    "Third Party Payroll & Background Verification services",
    "Technology-driven process to reduce costs and handle high-volume hiring",
  ],
  tags: ["Leadership Hiring", "C-Suite", "HR Outsourcing"],
  color: "#0f766e",
  bg: "rgba(15,118,110,0.1)",
  photo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=500",
  stat: "Board to GM level placements",
},
 {
  id: 2,
  icon: "Briefcase",
  title: "Third Party Payroll",
  tagline: "Streamline Your Business with Expert Payroll & HR Solutions",
  description:
    "Trust EduITalent for reliable third-party payroll solutions that empower your business growth. We provide advanced payroll technology and deep expertise to streamline your processes end-to-end — with a strong focus on confidentiality, accuracy, and compliance.",
  bullets: [
    "Complete payroll processing, tax filing & benefits administration",
    "Dedicated team of payroll experts assigned to your account",
    "Advanced payroll technology for accuracy and efficiency",
    "Customizable reporting, outsourcing & strategic consulting options",
  ],
  tags: ["Payroll", "HR Outsourcing", "Compliance"],
  color: "#db2777",
  bg: "rgba(219,39,119,0.1)",
  photo: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=500",
  stat: "End-to-end payroll management",
},
  {
  id: 3,
  icon: "Globe",
  title: "Custom Website Design",
  tagline: "Unique, Brand-Focused Online Presence",
  description:
    "We create custom websites tailored specifically to your business goals and audience — from responsive layouts to full ecommerce platforms. Every site we build is optimized for performance, search engines, and long-term scalability.",
  bullets: [
    "Responsive layouts that adapt seamlessly across all devices",
    "Ecommerce solutions with inventory management & secure payments",
    "CMS integration for easy, no-code content management",
    "SEO-optimized design with clean code and fast loading speeds",
  ],
  tags: ["Web Design", "Ecommerce", "SEO"],
  color: "#059669",
  bg: "rgba(5,150,105,0.1)",
  photo: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=500",
  stat: "Maintenance & support included",
},
 {
  id: 4,
  icon: "Smartphone",
  title: "Mobile App Development",
  tagline: "Build, Scale & Perform on Every Device",
  description:
    "We build high-performance Android, iOS, and cross-platform mobile applications that help businesses scale faster. From startups to enterprises, we deliver custom mobile solutions tailored to every business model — with a proven end-to-end development process.",
  bullets: [
    "Android, iOS & cross-platform (Flutter & React Native) development",
    "Business, Ecommerce, Healthcare, FinTech, Education & On-Demand apps",
    "Full-cycle process: Planning → UI/UX Design → Development → Launch",
    "Scalable, secure architecture with Apple & Google standard performance",
  ],
  tags: ["Android", "iOS", "Cross-Platform"],
  color: "#d97706",
  bg: "rgba(217,119,6,0.1)",
  photo: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=500",
  stat: "Android · iOS · Flutter · React Native",
},
  {
  id: 5,
  icon: "UserCheck",
  title: "Middle / Entry Level Hiring",
  tagline: "Right Talent at Every Level",
  description:
    "EduITalent is a specialist recruitment consultancy in India with a global reach, placing professionals across lower, middle, and senior management levels. Our Delhi-based recruiters work closely with hiring managers to understand specific needs and attract the best-fit talent across industries.",
  bullets: [
    "End-to-end recruitment: job postings to onboarding support",
    "Screening, interviews & reference checks for all seniority levels",
    "Background verification and skills assessments included",
    "Large active talent pool across diverse industries nationwide",
  ],
  tags: ["All Levels", "Pan-India", "End-to-End Hiring"],
  color: "#2563eb",
  bg: "rgba(37,99,235,0.1)",
  photo: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=500",
  stat: "Hiring across all management levels",
},
{
  id: 6,
  icon: "BarChart3",
  title: "Digital Marketing",
  tagline: "Your Vision, Our Strategy — Creativity That Performs",
  description:
    "Grow your business with smart, full-stack digital marketing solutions designed to attract, engage, and convert. Whether you're a startup or an established brand, we craft personalized campaigns that deliver real, measurable business growth.",
  bullets: [
    "Personalized campaigns tailored to your brand and target audience",
    "Transparent reporting, analytics & dedicated account manager",
    "Full-funnel strategies: awareness, engagement & conversion",
    "Scalable solutions for startups and enterprise brands alike",
  ],
  tags: ["SEO", "Campaigns", "Analytics"],
  color: "#7c3aed",
  bg: "rgba(124,58,237,0.1)",
  photo: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500",
  stat: "Real growth, transparent results",
},
];

const ServicesPage = () => {
  const [activeId, setActiveId] = useState(null);
  const isStandalone = window.location.pathname === "/services";

  const toggle = (id) => setActiveId((prev) => (prev === id ? null : id));

  return (
    <section id="services" className={`svc-section ${isStandalone ? 'standalone-page' : ''}`}>

      {/* Background */}
      <div className="svc-bg"></div>
      <div className="svc-bg-photo-strip">
        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400" alt="" className="svc-bg-img" />
        <div className="svc-bg-dim"></div>
      </div>
      <div className="svc-blob svc-blob-1"></div>
      <div className="svc-blob svc-blob-2"></div>
      <div className="svc-blob svc-blob-3"></div>
      <div className="svc-particles">
        {[...Array(18)].map((_, i) => <div key={i} className={`svc-dot svc-dot-${i+1}`}></div>)}
      </div>

      <div className="container svc-inner">

        {/* Header */}
        <div className="svc-header">
          <span className="svc-badge" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Sparkles size={14} /> What We Offer
          </span>
          <h2 className="svc-title">Services Built for <span className="svc-title-accent">Real Growth</span></h2>
          <p className="svc-subtitle">
            Six specialised practices. One mission — to help your business and people reach their highest potential.
          </p>
          <div className="svc-header-line"></div>
        </div>

        {/* Grid */}
        <div className="svc-grid">
          {servicesData.map((svc, i) => {
            const isActive = activeId === svc.id;
            return (
              <div
                key={svc.id}
                className={`svc-card${isActive ? " svc-card--open" : ""}`}
                style={{ "--c": svc.color, "--cb": svc.bg, animationDelay: `${i * 90}ms` }}
                onClick={() => toggle(svc.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle(svc.id)}
                aria-expanded={isActive}
              >
                {/* Card background photo */}
                <div className="svc-card-photo">
                  <img src={svc.photo} alt={svc.title} />
                  <div className="svc-card-photo-dim"></div>
                </div>

                {/* Glowing orb */}
                <div className="svc-card-orb"></div>

                {/* Icon + stat chip */}
                <div className="svc-card-top">
                  <div className="svc-icon-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span className="svc-icon" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {svcIconMap[svc.icon]}
                    </span>
                  </div>
                  <span className="svc-stat-chip">{svc.stat}</span>
                </div>

                {/* Titles */}
                <div className="svc-card-titles">
                  <p className="svc-tagline">{svc.tagline}</p>
                  <h3 className="svc-card-title">{svc.title}</h3>
                </div>

                {/* Collapsed preview */}
                {!isActive && (
                  <p className="svc-preview">{svc.description.slice(0, 90)}…</p>
                )}

                {/* Expanded */}
                {isActive && (
                  <div className="svc-expanded">
                    <p className="svc-desc">{svc.description}</p>
                    <ul className="svc-bullets">
                      {svc.bullets.map((b, j) => (
                        <li key={j} className="svc-bullet">
                          <span className="svc-bullet-dot"></span>
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className="svc-tags">
                      {svc.tags.map((t) => (
                        <span key={t} className="svc-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Chevron */}
                <div className={`svc-chevron${isActive ? " svc-chevron--open" : ""}`}>
                  <span>{isActive ? "Close ↑" : "Explore →"}</span>
                </div>

                {/* Bottom accent line */}
                <div className="svc-card-line"></div>
              </div>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="svc-cta-banner">
          <div className="svc-cta-bg-photo">
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200" alt="" />
            <div className="svc-cta-dim"></div>
          </div>
          <div className="svc-cta-content">
            <div className="svc-cta-left">
              <span className="svc-cta-eyebrow">Let's Talk</span>
              <h3 className="svc-cta-heading">Not sure which service fits your need?</h3>
              <p className="svc-cta-text">A 20-minute discovery call is all it takes. No pressure, no pitch — just a conversation.</p>
            </div>
            <div className="svc-cta-actions">
              <Link to="/contact" className="svc-cta-btn-primary">Book a Free Call →</Link>
              <Link to="/about" className="svc-cta-btn-ghost">Learn About Us</Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ServicesPage;
